import { useState, useEffect, useRef } from "react";
import { supabase } from "./db";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import SupplierLogs from "./SupplierLogs";

// 👇 FIXNÉ STĹPCE
const FIXED_COLUMNS = ["akcie", "status", "content_status", "internal_sku"];

const COLUMN_NAMES_MAP = {
  ean: "EAN Kód",
  company_name: "Dodávateľ",
  product_name: "Názov produktu",
  category: "Kategória na webe",
  price: "Cena výrobcu (€)",
  stock: "Sklad (ks)",
  brand: "Brand",
  created_at: "Dátum pridania",
  approved_at: "Čas rozhodnutia",
  weight_brutto_g: "Váha Brutto (g)",
  weight_netto_g: "Váha Netto (g)",
  short_description: "Krátky text",
  description: "Hlavný popis",
  image_url: "Obrázok",
  product_line: "Produktová rada",
  sukl: "ŠÚKL",
  adc: "ADC kód",
  predecessor: "Predchodca",
  dr_max_code: "Kód Dr.Max",
  base_mj: "Základná MJ",
  ratio_to_zmj: "Pomer k ZMJ",
  packaging: "Balenie",
  ingredients: "Zloženie",
  dosage: "Dávkovanie",
  nutritional_values: "Výživové hodnoty",
  width_mm: "Šírka (mm)",
  height_mm: "Výška (mm)",
  depth_mm: "Hĺbka (mm)",
  pieces_in_carton: "Ks v kartóne",
  pieces_on_pallet: "Ks na palete",
  manufacturer: "Výrobca",
  country_of_origin: "Krajina pôvodu",
  customs_code: "Colný sadzobník",
  recycling_fee: "Recyklačný poplatok",
  vat_percent: "% DPH",
  dmoc: "DMOC (€)",
  returnable_packaging: "Zálohovaný obal",
  total_weight: "Celková hmotnosť",
  listing_date: "Zalistovanie od",
  listing_type: "Typ listingu",
  admin_fee: "Admin poplatok",
  turnover_reward_percent: "Odplata z obratu (%)",
  tracked_transaction: "Sledovaná transakcia",
  tracked_price: "Sledovaná cena",
};

export default function AdminDashboard({ session }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const isManager = session?.user?.email === "manager@1.sk";

  const [dbColumns, setDbColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([
    "image_url",
    "company_name",
    "product_name",
    "category",
    "ean",
    "price",
    "stock",
    "created_at",
  ]);
  const [showColumnChooser, setShowColumnChooser] = useState(false);
  const columnChooserRef = useRef(null);

  const [colFilters, setColFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const [showExport, setShowExport] = useState(false);
  const [exportCompany, setExportCompany] = useState("");
  const [exportDateFrom, setExportDateFrom] = useState("");
  const [exportDateTo, setExportDateTo] = useState("");
  const [exportingImages, setExportingImages] = useState(false);

  // 👇 STAVY PRE STRÁNKOVANIE
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50); // Predvolený počet produktov na stranu

  useEffect(() => {
    fetchColumnsFromDB();
    fetchProducts();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        columnChooserRef.current &&
        !columnChooserRef.current.contains(event.target)
      ) {
        setShowColumnChooser(false);
      }
    };
    if (showColumnChooser)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showColumnChooser]);

  const fetchColumnsFromDB = async () => {
    const { data, error } = await supabase.rpc("get_products_columns");
    if (!error && data && data.length > 0) {
      const exclude = [
        "id",
        "supplier_id",
        "locked_by_master",
        "approved_at",
        "status",
        "content_status",
        "internal_sku",
        "updated_at",
      ];
      let cleanCols = data.filter((c) => !exclude.includes(c));
      if (!cleanCols.includes("company_name"))
        cleanCols.unshift("company_name");
      cleanCols.sort((a, b) =>
        (COLUMN_NAMES_MAP[a] || a).localeCompare(COLUMN_NAMES_MAP[b] || b)
      );
      setDbColumns(cleanCols);
    }
  };

  const fetchProducts = async () => {
    const { data, error } = await supabase
      .from("products")
      .select(`*, profiles (company_name, contact_email)`)
      .order("created_at", { ascending: false });
    if (data) setProducts(data);
    if (error) console.error("Chyba pri načítaní:", error);
    setLoading(false);
  };

  const writeLog = async (action, productName, details) => {
    try {
      await supabase
        .from("activity_logs")
        .insert([
          {
            user_email: session.user.email,
            action: action,
            product_name: productName,
            details: details,
          },
        ]);
    } catch (err) {
      console.error("Kritická chyba logovania:", err);
    }
  };

  const handleDelete = async (product) => {
    if (
      !window.confirm(
        `Naozaj chcete natrvalo vymazať produkt: ${product.product_name}?`
      )
    )
      return;
    const { error } = await supabase
      .from("products")
      .delete()
      .eq("id", product.id);
    if (!error) {
      await writeLog("DELETE", product.product_name, { id: product.id });
      setProducts(products.filter((p) => p.id !== product.id));
    } else {
      alert("Chyba pri mazaní: " + error.message);
    }
  };

  const startEdit = (product) => {
    setEditingId(product.id);
    setEditForm({ ...product });
  };

  const handleSave = async () => {
    const {
      profiles,
      created_at,
      supplier_id,
      approved_at,
      status,
      ...updateData
    } = editForm;
    const payload = isManager
      ? { ...updateData, locked_by_master: true }
      : updateData;
    const { error } = await supabase
      .from("products")
      .update(payload)
      .eq("id", editingId);
    if (!error) {
      await writeLog("UPDATE", updateData.product_name, updateData);
      setProducts(
        products.map((p) => (p.id === editingId ? { ...p, ...payload } : p))
      );
      setEditingId(null);
    } else {
      alert("Chyba pri ukladaní: " + error.message);
    }
  };

  const handleStatusChange = async (product, newStatus) => {
    const timestamp = new Date().toISOString();
    const updatePayload = {
      status: newStatus,
      approved_at: timestamp,
      locked_by_master: isManager ? true : product.locked_by_master,
    };
    const { error } = await supabase
      .from("products")
      .update(updatePayload)
      .eq("id", product.id);
    if (!error) {
      await writeLog("STATUS_CHANGE", product.product_name, { newStatus });
      setProducts(
        products.map((p) =>
          p.id === product.id ? { ...p, ...updatePayload } : p
        )
      );
    } else {
      alert("Chyba pri zmene statusu: " + error.message);
    }
  };

  // FILTRÁCIA
  const filteredProducts = products.filter((p) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesGlobal =
      !searchTerm ||
      [p.product_name, p.ean, p.profiles?.company_name, p.internal_sku].some(
        (val) =>
          String(val || "")
            .toLowerCase()
            .includes(searchLower)
      );

    if (!matchesGlobal) return false;

    const activeFilters = Object.entries(colFilters).filter(
      ([, val]) => val !== ""
    );
    for (const [key, filterVal] of activeFilters) {
      let cellVal = key === "company_name" ? p.profiles?.company_name : p[key];
      if (key === "status" && !cellVal) cellVal = "Čaká na schválenie";
      if (key === "content_status" && !cellVal) cellVal = "Čaká na content";
      if (
        !String(cellVal || "")
          .toLowerCase()
          .includes(filterVal.toLowerCase())
      )
        return false;
    }
    return true;
  });

  // ZORAĎOVANIE
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (!sortConfig.key) return 0;
    let valA =
      sortConfig.key === "company_name"
        ? a.profiles?.company_name
        : a[sortConfig.key];
    let valB =
      sortConfig.key === "company_name"
        ? b.profiles?.company_name
        : b[sortConfig.key];

    if (sortConfig.key === "created_at" || sortConfig.key === "approved_at") {
      valA = new Date(valA || 0).getTime();
      valB = new Date(valB || 0).getTime();
    } else if (!isNaN(parseFloat(valA)) && !isNaN(parseFloat(valB))) {
      valA = parseFloat(valA) || 0;
      valB = parseFloat(valB) || 0;
    } else {
      valA = String(valA || "").toLowerCase();
      valB = String(valB || "").toLowerCase();
    }

    if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
    if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  // 👇 LOGIKA STRÁNKOVANIA (Aplikuje sa AŽ PO filtrácii a zoradení)
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  // Zobrazené produkty na aktuálnej strane
  const currentItems =
    itemsPerPage === "all"
      ? sortedProducts
      : sortedProducts.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages =
    itemsPerPage === "all"
      ? 1
      : Math.ceil(sortedProducts.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Ak sa zmení filter alebo vyhľadávanie, vrátime sa na 1. stranu
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, colFilters]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc")
      direction = "desc";
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) return " ↕️";
    return sortConfig.direction === "asc" ? " 🔼" : " 🔽";
  };

  const updateColFilter = (field, value) => {
    setColFilters((prev) => ({ ...prev, [field]: value }));
  };

  // EXPORT
  const handleExport = () => {
    let dataToExport = sortedProducts; // Exportujeme VŽDY všetky vyfiltrované, nielen tie na aktuálnej strane
    if (exportCompany)
      dataToExport = dataToExport.filter(
        (p) => p.profiles?.company_name === exportCompany
      );
    if (exportDateFrom)
      dataToExport = dataToExport.filter(
        (p) => new Date(p.created_at) >= new Date(exportDateFrom)
      );
    if (exportDateTo) {
      const toDate = new Date(exportDateTo);
      toDate.setHours(23, 59, 59, 999);
      dataToExport = dataToExport.filter(
        (p) => new Date(p.created_at) <= toDate
      );
    }

    const excelData = dataToExport.map((p) => {
      const row = {};
      row["Status"] = p.status || "Čaká na schválenie";
      row["Čas rozhodnutia"] = p.approved_at
        ? new Date(p.approved_at).toLocaleString("sk-SK")
        : "-";
      row["Web Status"] = p.content_status || "Čaká na content";
      row["Interné SKU"] = p.internal_sku || "-";

      selectedColumns.forEach((col) => {
        if (col === "image_url") return;
        if (col === "company_name") {
          row[COLUMN_NAMES_MAP[col]] = p.profiles?.company_name;
          return;
        }

        const label = COLUMN_NAMES_MAP[col] || col;
        row[label] =
          col === "created_at"
            ? new Date(p[col]).toLocaleDateString("sk-SK")
            : p[col];
      });
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Produkty");
    XLSX.writeFile(wb, `Export_${exportCompany || "Katalog"}.xlsx`);
    setShowExport(false);
  };

  const handleImageExport = async () => {
    let toExport = sortedProducts;
    if (exportCompany)
      toExport = toExport.filter(
        (p) => p.profiles?.company_name === exportCompany
      );
    const itemsWithImages = toExport.filter((p) => p.image_url && p.ean);
    if (itemsWithImages.length === 0) {
      alert("⚠️ Žiadne produkty s obrázkami neboli nájdené.");
      return;
    }

    setExportingImages(true);
    const zip = new JSZip();
    try {
      for (const item of itemsWithImages) {
        const path = item.image_url.split("product-images/")[1];
        if (path) {
          const { data: blob, error } = await supabase.storage
            .from("product-images")
            .download(path);
          if (error) continue;
          const extension =
            item.image_url.split(".").pop().split(/\#|\?/)[0] || "jpg";
          zip.file(`${item.ean}.${extension}`, blob);
        }
      }
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `Obrazky_${exportCompany || "Vsetky"}.zip`);
      setShowExport(false);
    } catch (err) {
      alert("Chyba pri balení obrázkov.");
    } finally {
      setExportingImages(false);
    }
  };

  const renderStatusBadge = (status, approvedAt) => {
    const currentStatus = status || "Čaká na schválenie";
    const badgeStyle =
      currentStatus === "Schválené"
        ? badgeSuccess
        : currentStatus === "Neschválené"
        ? badgeDanger
        : badgeWarning;
    return (
      <div>
        <span style={badgeStyle}>{currentStatus}</span>
        <br />
        {approvedAt && (
          <small
            style={{
              color: "#64748b",
              fontSize: "11px",
              display: "inline-block",
              marginTop: "4px",
            }}
          >
            {new Date(approvedAt).toLocaleString("sk-SK")}
          </small>
        )}
      </div>
    );
  };

  return (
    <div style={{ maxWidth: "100%", padding: "0 20px" }}>
      {/* HLAVIČKA A NÁSTROJE */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h2>👑 Kompletný katalóg produktov (Admin)</h2>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <div style={{ position: "relative" }} ref={columnChooserRef}>
            <button
              onClick={() => setShowColumnChooser(!showColumnChooser)}
              className="btn-outline"
              style={{ display: "flex", gap: "8px", alignItems: "center" }}
            >
              ⚙️ Zobraziť stĺpce (
              {selectedColumns.length + FIXED_COLUMNS.length})
            </button>
            {showColumnChooser && (
              <div style={columnChooserPopupStyle}>
                <div style={popupHeaderStyle}>Vyberte stĺpce pre výpis:</div>
                <div style={popupContentStyle}>
                  {FIXED_COLUMNS.map((col) => (
                    <label key={col} style={popupItemDisabledStyle}>
                      <input
                        type="checkbox"
                        checked
                        disabled
                        style={{
                          cursor: "not-allowed",
                          width: "16px",
                          height: "16px",
                        }}
                      />
                      <span style={{ flex: 1 }}>
                        {col === "akcie"
                          ? "Akcie"
                          : col === "status"
                          ? "Status"
                          : col === "content_status"
                          ? "Web Status"
                          : "Interné SKU"}
                      </span>
                      <span
                        style={{
                          color: "#94a3b8",
                          fontSize: "11px",
                          fontStyle: "italic",
                        }}
                      >
                        (fixný)
                      </span>
                    </label>
                  ))}
                  {dbColumns.map((col) => (
                    <label key={col} style={popupItemStyle}>
                      <input
                        type="checkbox"
                        checked={selectedColumns.includes(col)}
                        onChange={(e) => {
                          if (e.target.checked)
                            setSelectedColumns([...selectedColumns, col]);
                          else
                            setSelectedColumns(
                              selectedColumns.filter((c) => c !== col)
                            );
                        }}
                        style={{
                          width: "16px",
                          height: "16px",
                          cursor: "pointer",
                        }}
                      />
                      <span style={{ flex: 1 }}>
                        {COLUMN_NAMES_MAP[col] || col}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => setShowExport(!showExport)}
            className="btn-outline"
          >
            📥 Nastaviť Export
          </button>
        </div>
      </div>

      <SupplierLogs />

      {/* EXPORTNÉ NÁSTROJE */}
      {showExport && (
        <div
          className="card"
          style={{
            padding: "20px",
            marginBottom: "20px",
            background: "#f8fafc",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "15px",
              flexWrap: "wrap",
              alignItems: "flex-end",
            }}
          >
            <select
              onChange={(e) => setExportCompany(e.target.value)}
              style={{
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
              }}
            >
              <option value="">Všetky firmy</option>
              {[...new Set(products.map((p) => p.profiles?.company_name))]
                .filter(Boolean)
                .map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
            </select>
            <input
              type="date"
              onChange={(e) => setExportDateFrom(e.target.value)}
              style={{
                padding: "8px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
              }}
            />
            <input
              type="date"
              onChange={(e) => setExportDateTo(e.target.value)}
              style={{
                padding: "8px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
              }}
            />
            <button onClick={handleExport} className="btn-outline">
              💾 Excel
            </button>
            <button
              onClick={handleImageExport}
              className="btn-success"
              disabled={exportingImages}
            >
              {exportingImages ? "Balím..." : "ZIP Obrázkov"}
            </button>
          </div>
        </div>
      )}

      {/* GLOBÁLNE VYHĽADÁVANIE */}
      <div className="card" style={{ padding: "20px", marginBottom: "20px" }}>
        <input
          type="text"
          placeholder="🔍 Globálne hľadanie (Názov, EAN, Dodávateľ, SKU)..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: "100%",
            maxWidth: "450px",
            padding: "12px 16px",
            borderRadius: "10px",
            border: "1px solid #cbd5e1",
            outline: "none",
            fontSize: "14px",
          }}
        />
      </div>

      {/* HLAVNÁ TABUĽKA */}
      <div
        className="card"
        style={{
          padding: "0",
          overflowX: "auto",
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          minHeight: "500px",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
          }}
        >
          <thead>
            <tr
              style={{
                background: "#f8fafc",
                borderBottom: "2px solid #e2e8f0",
              }}
            >
              <th style={thStyle}>Akcie</th>

              <th style={thStyle}>
                <div
                  style={sortableHeaderContent}
                  onClick={() => requestSort("status")}
                >
                  Status {getSortIcon("status")}
                </div>
                <input
                  type="text"
                  style={filterInputStyle}
                  placeholder="Filter..."
                  onChange={(e) => updateColFilter("status", e.target.value)}
                />
              </th>

              <th
                style={{
                  ...thStyle,
                  backgroundColor: "#f0fdf4",
                  minWidth: "160px",
                }}
              >
                <div
                  style={sortableHeaderContent}
                  onClick={() => requestSort("content_status")}
                >
                  Web Status {getSortIcon("content_status")}
                </div>
                <input
                  type="text"
                  style={filterInputStyle}
                  placeholder="Filter..."
                  onChange={(e) =>
                    updateColFilter("content_status", e.target.value)
                  }
                />
              </th>

              <th
                style={{
                  ...thStyle,
                  backgroundColor: "#fffbeb",
                  minWidth: "160px",
                }}
              >
                <div
                  style={sortableHeaderContent}
                  onClick={() => requestSort("internal_sku")}
                >
                  Interné SKU {getSortIcon("internal_sku")}
                </div>
                <input
                  type="text"
                  style={filterInputStyle}
                  placeholder="Filter SKU..."
                  onChange={(e) =>
                    updateColFilter("internal_sku", e.target.value)
                  }
                />
              </th>

              {selectedColumns.map((colKey) => {
                const label = COLUMN_NAMES_MAP[colKey] || colKey;
                const minWidth =
                  colKey === "image_url"
                    ? "60px"
                    : colKey === "short_description" || colKey === "description"
                    ? "300px"
                    : "160px";

                return (
                  <th key={colKey} style={{ ...thStyle, minWidth }}>
                    {colKey === "image_url" ? (
                      <div style={sortableHeaderContent}>Foto</div>
                    ) : (
                      <>
                        <div
                          style={sortableHeaderContent}
                          onClick={() => requestSort(colKey)}
                        >
                          {label} {getSortIcon(colKey)}
                        </div>
                        <input
                          type="text"
                          style={filterInputStyle}
                          placeholder={`Filter...`}
                          onChange={(e) =>
                            updateColFilter(colKey, e.target.value)
                          }
                        />
                      </>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={selectedColumns.length + 4}
                  style={{ textAlign: "center", padding: "40px" }}
                >
                  Načítavam produkty...
                </td>
              </tr>
            ) : currentItems.length === 0 ? (
              <tr>
                <td
                  colSpan={selectedColumns.length + 4}
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#64748b",
                  }}
                >
                  Zadaným filtrom nezodpovedá žiadny produkt.
                </td>
              </tr>
            ) : (
              currentItems.map((p) => (
                <tr
                  key={p.id}
                  style={{
                    borderBottom: "1px solid #e2e8f0",
                    background:
                      p.status === "Neschválené" ? "#fef2f2" : "transparent",
                    transition: "background-color 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      p.status === "Neschválené" ? "#fee2e2" : "#f8fafc")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      p.status === "Neschválené" ? "#fef2f2" : "transparent")
                  }
                >
                  {/* Akcie */}
                  <td style={tdStyle}>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      {p.locked_by_master && !isManager ? (
                        <span
                          style={{
                            color: "#1e40af",
                            fontSize: "11px",
                            fontWeight: "700",
                            background: "#dbeafe",
                            padding: "4px 8px",
                            borderRadius: "6px",
                          }}
                        >
                          🔒 Váš manažér editoval produkt
                        </span>
                      ) : editingId === p.id ? (
                        <>
                          <button
                            onClick={handleSave}
                            title="Uložiť"
                            style={actionBtnStyle}
                          >
                            💾
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            title="Zrušiť"
                            style={actionBtnStyle}
                          >
                            ❌
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleStatusChange(p, "Schválené")}
                            title="Schváliť"
                            style={actionBtnStyle}
                          >
                            ✅
                          </button>
                          <button
                            onClick={() => handleStatusChange(p, "Neschválené")}
                            title="Zamietnuť"
                            style={actionBtnStyle}
                          >
                            🚫
                          </button>
                          <div
                            style={{
                              width: "1px",
                              height: "20px",
                              background: "#cbd5e1",
                              margin: "0 5px",
                            }}
                          ></div>
                          <button
                            onClick={() => startEdit(p)}
                            title="Editovať"
                            style={actionBtnStyle}
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDelete(p)}
                            title="Zmazať"
                            style={actionBtnStyle}
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </div>
                  </td>

                  {/* Status */}
                  <td style={tdStyle}>
                    {renderStatusBadge(p.status, p.approved_at)}
                  </td>

                  {/* Web Status */}
                  <td style={{ ...tdStyle, backgroundColor: "#f0fdf4" }}>
                    {p.content_status ? (
                      <span
                        style={{
                          display: "inline-block",
                          whiteSpace: "nowrap",
                          backgroundColor:
                            p.content_status === "Zalistované na webe"
                              ? "#dcfce7"
                              : "#f3f4f6",
                          color:
                            p.content_status === "Zalistované na webe"
                              ? "#166534"
                              : "#475569",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: "bold",
                          border: "1px solid #cbd5e1",
                        }}
                      >
                        {p.content_status}
                      </span>
                    ) : (
                      <span
                        style={{
                          color: "#94a3b8",
                          fontSize: "11px",
                          fontStyle: "italic",
                        }}
                      >
                        Čaká na content
                      </span>
                    )}
                  </td>

                  {/* Interné SKU */}
                  <td
                    style={{
                      ...tdStyle,
                      fontWeight: "bold",
                      color: "#b45309",
                      backgroundColor: "#fffbeb",
                    }}
                  >
                    {editingId === p.id ? (
                      <input
                        type="text"
                        value={editForm.internal_sku || ""}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            internal_sku: e.target.value,
                          })
                        }
                        style={editInputStyle}
                      />
                    ) : p.internal_sku ? (
                      p.internal_sku
                    ) : (
                      <span
                        style={{
                          color: "#94a3b8",
                          fontWeight: "normal",
                          fontSize: "12px",
                        }}
                      >
                        Nové
                      </span>
                    )}
                  </td>

                  {/* Dynamické stĺpce */}
                  {selectedColumns.map((colKey) => (
                    <td key={colKey} style={tdStyle}>
                      {colKey === "image_url" ? (
                        p.image_url ? (
                          <img
                            src={p.image_url}
                            style={{
                              width: "40px",
                              borderRadius: "4px",
                              objectFit: "cover",
                            }}
                            alt="produkt"
                          />
                        ) : (
                          "-"
                        )
                      ) : colKey === "created_at" ? (
                        new Date(p.created_at).toLocaleDateString("sk-SK")
                      ) : colKey === "company_name" ? (
                        <strong style={{ color: "#334155" }}>
                          {p.profiles?.company_name || "-"}
                        </strong>
                      ) : editingId === p.id ? (
                        <input
                          type="text"
                          value={editForm[colKey] || ""}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              [colKey]: e.target.value,
                            })
                          }
                          style={editInputStyle}
                        />
                      ) : (
                        <div
                          style={
                            colKey === "short_description" ||
                            colKey === "description"
                              ? multiEllipsisStyle
                              : ellipsisStyle
                          }
                          title={p[colKey]}
                        >
                          {p[colKey] || "-"}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* 👇 OVLÁDANIE STRÁNKOVANIA (PAGINATION) 👇 */}
        {sortedProducts.length > 0 && (
          <div style={paginationContainerStyle}>
            <div style={{ fontSize: "13px", color: "#64748b" }}>
              Zobrazených {indexOfFirstItem + 1} -{" "}
              {Math.min(indexOfLastItem, sortedProducts.length)} z{" "}
              <strong>{sortedProducts.length}</strong> produktov
            </div>

            <div style={{ display: "flex", gap: "5px" }}>
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1 || itemsPerPage === "all"}
                style={pageBtnStyle(
                  currentPage === 1 || itemsPerPage === "all"
                )}
              >
                Predchádzajúca
              </button>

              {/* Čísla strán (Zobrazí len ak nie je zvolené 'Všetky') */}
              {itemsPerPage !== "all" &&
                Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (number) => {
                    // Skryjeme strany, ktoré sú príliš ďaleko od aktuálnej (aby tam nebolo 50 tlačidiel)
                    if (
                      number === 1 ||
                      number === totalPages ||
                      (number >= currentPage - 2 && number <= currentPage + 2)
                    ) {
                      return (
                        <button
                          key={number}
                          onClick={() => paginate(number)}
                          style={
                            currentPage === number
                              ? activePageBtnStyle
                              : pageBtnStyle(false)
                          }
                        >
                          {number}
                        </button>
                      );
                    } else if (
                      number === currentPage - 3 ||
                      number === currentPage + 3
                    ) {
                      return (
                        <span
                          key={number}
                          style={{ padding: "5px 10px", color: "#94a3b8" }}
                        >
                          ...
                        </span>
                      );
                    }
                    return null;
                  }
                )}

              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages || itemsPerPage === "all"}
                style={pageBtnStyle(
                  currentPage === totalPages || itemsPerPage === "all"
                )}
              >
                Ďalšia
              </button>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontSize: "13px",
                color: "#475569",
              }}
            >
              <span>Zobraziť po:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(
                    e.target.value === "all" ? "all" : Number(e.target.value)
                  );
                  setCurrentPage(1);
                }}
                style={pageSelectStyle}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value="all">Všetky</option>
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// 🔥 CSS-IN-JS STYLY

const thStyle = {
  padding: "12px 15px",
  color: "#475569",
  fontSize: "13px",
  textAlign: "left",
  verticalAlign: "top",
  whiteSpace: "nowrap",
  borderLeft: "1px solid #e2e8f0",
};

const sortableHeaderContent = {
  display: "flex",
  alignItems: "center",
  gap: "5px",
  cursor: "pointer",
  fontWeight: "bold",
  userSelect: "none",
};

const tdStyle = {
  padding: "12px 15px",
  verticalAlign: "middle",
  borderLeft: "1px solid #e2e8f0",
};

const filterInputStyle = {
  display: "block",
  width: "100%",
  padding: "6px 8px",
  fontSize: "11px",
  marginTop: "8px",
  borderRadius: "6px",
  border: "1px solid #cbd5e1",
  outline: "none",
  boxSizing: "border-box",
  backgroundColor: "white",
};

const editInputStyle = {
  padding: "6px",
  width: "100%",
  borderRadius: "6px",
  border: "1px solid #cbd5e1",
  outline: "none",
  boxSizing: "border-box",
};

const ellipsisStyle = {
  maxWidth: "200px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const multiEllipsisStyle = {
  maxWidth: "350px",
  display: "-webkit-box",
  webkitLineClamp: "3",
  webkitBoxOrient: "vertical",
  overflow: "hidden",
};

const actionBtnStyle = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  fontSize: "16px",
  cursor: "pointer",
  padding: "6px",
  borderRadius: "8px",
  lineHeight: "1",
  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  transition: "all 0.2s",
};

const badgeSuccess = {
  background: "#dcfce7",
  color: "#166534",
  padding: "4px 8px",
  borderRadius: "12px",
  fontSize: "11px",
  fontWeight: "bold",
};
const badgeDanger = {
  background: "#fee2e2",
  color: "#991b1b",
  padding: "4px 8px",
  borderRadius: "12px",
  fontSize: "11px",
  fontWeight: "bold",
};
const badgeWarning = {
  background: "#fef3c7",
  color: "#92400e",
  padding: "4px 8px",
  borderRadius: "12px",
  fontSize: "11px",
  fontWeight: "bold",
};

// STYLY PRE MENU STĹPCOV
const columnChooserPopupStyle = {
  position: "absolute",
  top: "100%",
  left: "0",
  marginTop: "12px",
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "16px",
  padding: "20px",
  boxShadow:
    "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
  zIndex: 1000,
  width: "320px",
};

const popupHeaderStyle = {
  fontWeight: "800",
  fontSize: "15px",
  color: "#1e293b",
  marginBottom: "15px",
  paddingBottom: "10px",
  borderBottom: "1px solid #e2e8f0",
  textAlign: "left",
};

const popupContentStyle = {
  maxHeight: "350px",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
};

const popupItemStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: "10px",
  padding: "6px 8px",
  fontSize: "13px",
  color: "#334155",
  cursor: "pointer",
  borderRadius: "8px",
  userSelect: "none",
  margin: "0",
  listStyle: "none",
  textAlign: "left",
  transition: "background-color 0.2s",
};

const popupItemDisabledStyle = {
  ...popupItemStyle,
  color: "#94a3b8",
  cursor: "not-allowed",
  backgroundColor: "#f8fafc",
};

// 👇 STYLY PRE STRÁNKOVANIE 👇
const paginationContainerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "15px 20px",
  borderTop: "1px solid #e2e8f0",
  backgroundColor: "#f8fafc",
  flexWrap: "wrap",
  gap: "15px",
};

const pageBtnStyle = (disabled) => ({
  padding: "6px 12px",
  border: "1px solid #cbd5e1",
  backgroundColor: disabled ? "#f1f5f9" : "white",
  color: disabled ? "#94a3b8" : "#334155",
  borderRadius: "6px",
  cursor: disabled ? "not-allowed" : "pointer",
  fontSize: "13px",
  fontWeight: "600",
  transition: "all 0.2s",
});

const activePageBtnStyle = {
  ...pageBtnStyle(false),
  backgroundColor: "#0f172a",
  color: "white",
  borderColor: "#0f172a",
};

const pageSelectStyle = {
  padding: "6px 10px",
  borderRadius: "6px",
  border: "1px solid #cbd5e1",
  fontSize: "13px",
  outline: "none",
  cursor: "pointer",
  backgroundColor: "white",
};
